import { useCallback } from 'react';
import styled from 'styled-components';

import {
  useRfqBuilderActions,
  useRfqBuilderView,
  useRfqBuilderCategorizedLegView,
} from '@paradigm/features/src/unified-rfqs/ui';

import { lima, razzmatazz } from '@paradigm/design-system/src/colors';

import { toast } from '#/components/UnifiedRfqs/toast';
import { MOBILE_BREAKPOINT } from '#/containers/layout/constants';
import { useTrackAction } from '@paradigm/features/src/analytics';

const ACTION_COLOR_MAP = {
  BUY: lima,
  SELL: razzmatazz,
};

export default function ActionSelector() {
  const { strategy, strategyShortcut } = useRfqBuilderView();
  const view = useRfqBuilderCategorizedLegView();
  const { toggleLegAction } = useRfqBuilderActions();
  const trackAction = useTrackAction();

  const strategyCode =
    strategyShortcut.selectedOption?.value ??
    strategy.selectedOption?.value ??
    null;

  const handleClick = useCallback(() => {
    trackAction('update strategy leg', {
      strategyCode,
      side: view.action.selectedOption,
      isHedge: view.isHedge,
    });

    if (strategy.selectedOption == null)
      throw new TypeError(`Strategy should be selected at this point`);
    const reaction = toggleLegAction(view.index, strategy.selectedOption.value);
    if (!reaction.ok) toast(reaction.message);
  }, [
    strategy.selectedOption,
    strategyCode,
    toggleLegAction,
    trackAction,
    view.action.selectedOption,
    view.index,
    view.isHedge,
  ]);

  const { action } = view;

  const color = getColor(action.selectedOption);
  return (
    <ActionPill
      color={color}
      onClick={handleClick}
      disabled={action.isDisabled}
    >
      {action.selectedOption}
    </ActionPill>
  );
}

const ActionPill = styled.button.withConfig<{
  readonly color: string;
}>({ shouldForwardProp: (prop) => prop !== 'color' })`
  background-color: ${({ color }) => color};
  color: #fff;
  display: inline-block;
  border: none;
  border-radius: 9px;
  font-size: 0.7rem;
  font-weight: 500;
  text-align: center;
  line-height: 1.125rem;
  padding: 0;
  width: 40px;

  @media screen and (max-width: ${MOBILE_BREAKPOINT}px) {
    line-height: 1.5rem;
    border-radius: 0.75rem;
    font-size: 0.75rem;
    padding: 0 1rem;
    width: auto;
    font-weight: bold;
    margin-right: 1rem;
  }
`;

function getColor(action: string) {
  const color = (ACTION_COLOR_MAP as Record<string, string>)[action];
  if (color == null)
    throw new TypeError(`There isn't a color for action "${action}"`);
  return color;
}
